import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { cn } from '../../../lib/utils';

interface OneTimeCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  length: number;
  disabled?: boolean;
  autoFocus?: boolean;
  ariaLabel: string;
  inputRef?: React.Ref<HTMLInputElement>;
}

const sanitizeDigits = (raw: string, maxLen: number): string =>
  (raw || '').replace(/\D/g, '').slice(0, maxLen);

export const OneTimeCodeInput: React.FC<OneTimeCodeInputProps> = ({
  value,
  onChange,
  length,
  disabled = false,
  autoFocus = false,
  ariaLabel,
  inputRef
}) => {
  const internalInputRef = useRef<HTMLInputElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const digits = useMemo(() => {
    const v = sanitizeDigits(value, length);
    const arr = Array.from({ length }, (_, i) => v[i] ?? '');
    return { value: v, arr };
  }, [length, value]);

  // Keep caret at the end so typing feels natural.
  useEffect(() => {
    if (!isFocused) return;
    const el = internalInputRef.current;
    if (!el) return;
    const pos = digits.value.length;
    try {
      el.setSelectionRange(pos, pos);
    } catch {
      // no-op
    }
  }, [digits.value.length, isFocused]);

  const setRefs = useCallback(
    (el: HTMLInputElement | null) => {
      internalInputRef.current = el;
      if (!inputRef) return;
      if (typeof inputRef === 'function') {
        inputRef(el);
        return;
      }
      try {
        (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
          el;
      } catch {
        // no-op
      }
    },
    [inputRef]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(sanitizeDigits(e.target.value, length));
    },
    [length, onChange]
  );

  const activeIndex = Math.min(digits.value.length, length - 1);

  return (
    <div
      className={cn(
        'relative inline-flex items-center gap-1 rounded-2xl border border-gray-100 bg-gray-50/80 p-3',
        'transition-colors duration-150 focus-within:ring-2 focus-within:ring-emerald-400',
        disabled ? 'opacity-80' : 'hover:bg-gray-50'
      )}
    >
      <input
        ref={setRefs}
        value={digits.value}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        autoFocus={autoFocus}
        disabled={disabled}
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        enterKeyHint="done"
        aria-label={ariaLabel}
        className="absolute inset-0 h-full w-full rounded-2xl bg-transparent opacity-0 outline-hidden"
      />

      {/* First group of 3 */}
      {[0, 1, 2].map(index => {
        const filled = Boolean(digits.arr[index]);
        const isActive = isFocused && index === activeIndex;
        return (
          <span
            key={index}
            className={cn(
              'flex h-12 w-10 items-center justify-center rounded-xl text-base font-semibold',
              'transition-colors duration-150',
              filled
                ? 'bg-emerald-500 text-white'
                : 'bg-white text-gray-900 ring-1 ring-gray-200',
              isActive && !filled ? 'ring-2 ring-emerald-400' : ''
            )}
            aria-hidden="true"
          >
            {digits.arr[index] || ''}
          </span>
        );
      })}

      {/* Separator dot */}
      <span
        className="mx-1 block h-1.5 w-1.5 rounded-full bg-gray-300"
        aria-hidden="true"
      />

      {/* Second group of 3 */}
      {[3, 4, 5].map(index => {
        const filled = Boolean(digits.arr[index]);
        const isActive = isFocused && index === activeIndex;
        return (
          <span
            key={index}
            className={cn(
              'flex h-12 w-10 items-center justify-center rounded-xl text-base font-semibold',
              'transition-colors duration-150',
              filled
                ? 'bg-emerald-500 text-white'
                : 'bg-white text-gray-900 ring-1 ring-gray-200',
              isActive && !filled ? 'ring-2 ring-emerald-400' : ''
            )}
            aria-hidden="true"
          >
            {digits.arr[index] || ''}
          </span>
        );
      })}
    </div>
  );
};

export default OneTimeCodeInput;
